<template>
    <div>
        <DRow>
            <DCol>
                <DTabs class="mb-3">
                    <DTabItem
                        id="open"
                        :active="tab === 'open'"
                        :label="__('contracts.terminations.open', { count:counters.open})"
                        :route-url="createRouteForTerminationTab('open')"
                    />
                    <DTabItem
                        id="due"
                        :active="tab === 'due'"
                        :label="__('contracts.terminations.due', { count:counters.due})"
                        :route-url="createRouteForTerminationTab('due')"
                    />
                    <DTabItem
                        id="suspended"
                        :active="tab === 'suspended'"
                        :label="__('contracts.terminations.suspended', { count:counters.suspended})"
                        :route-url="createRouteForTerminationTab('suspended')"
                    />
                    <DTabItem
                        id="completed"
                        :active="tab === 'completed'"
                        :label="__('contracts.terminations.completed', { count:counters.completed})"
                        :route-url="createRouteForTerminationTab('completed')"
                    />
                </DTabs>
            </DCol>
        </DRow>
        <DCard padding>
            <DForm @submit.prevent="search">
                <DRow
                    align-vertical="start"
                    class="align-items-end mb-3"
                >
                    <DCol lg="2">
                        <DInput
                            id="search"
                            v-model="queryString"
                            class="mb-0"
                            :label="__('contracts.terminations.search')"
                            maxlength="200"
                            @keydown.enter.exact.prevent="search"
                        />
                    </DCol>
                    <DCol sm="2">
                        <label class="form-label">{{ __('contracts.terminations.modules') }}</label>
                        <DMultiSelect
                            id="ff-modules-dropdown"
                            :options="moduleOptions"
                            :search="true"
                            :selection="true"
                            :selected="selectedModulesList"
                            :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                            selection-type="text"
                            @update="onSelectedModulesChange"
                        />
                    </DCol>
                    <DCol lg="2">
                        <label class="form-label">{{ __('contracts.terminations.select_segments') }}</label>
                        <DMultiSelect
                            id="segments-dropdown"
                            :options="filterSegmentsOptions"
                            selection-type="text"
                            sort-options
                            :selected="selectedSegmentsList"
                            @update="onSegmentsFilterChange"
                        />
                    </DCol>
                    <DCol lg="2">
                        <label class="form-label">{{ __('contracts.terminations.type') }}</label>
                        <DMultiSelect
                            id="type-dropdown"
                            :options="filterTypesOptions"
                            selection-type="text"
                            sort-options
                            :selected="selectedTypeList"
                            @update="onTypesFilterChange"
                        />
                    </DCol>
                    <DCol lg="2">
                        <label class="form-label">{{ __('contracts.terminations.reason') }}</label>
                        <DMultiSelect
                            id="reason-dropdown"
                            :options="filterReasonOptions"
                            selection-type="text"
                            sort-options
                            :selected="selectedReasonList"
                            @update="onReasonsFilterChange"
                        />
                    </DCol>
                    <DCol lg="2">
                        <label class="form-label">{{ __('contracts.terminations.select_brands') }}</label>
                        <DMultiSelect
                            id="brands-dropdown"
                            :options="filterBrandOptions"
                            selection-type="text"
                            sort-options
                            :selected="selectedBrandList"
                            @update="onBrandsFilterChange"
                        />
                    </DCol>
                </DRow>
                <DRow
                    align-vertical="start"
                    class="align-items-end mb-3"
                >
                    <DCol
                        lg="2"
                    >
                        <label class="form-label">{{ __('contracts.terminations.contract_status') }}</label>
                        <DSelect
                            id="status-dropdown"
                            :value="selectedContractStatusList"
                            :options="contractStatuses"
                            @update:value="onContractStatusChange"
                        />
                    </DCol>
                </DRow>
                <DRow
                    align-vertical="start"
                    class="align-items-end mb-3"
                >
                    <DCol
                        lg="3"
                        class="d-flex gap-2"
                    >
                        <DButton
                            id="filter-apply"
                            :label="__('messages.actions.filter')"
                            @click="search"
                        />
                        <DButton
                            id="filter-reset"
                            icon="filter-off"
                            :label="__('messages.actions.reset_filter')"
                            @click="resetFilters"
                        />
                    </DCol>
                </DRow>
            </DForm>

            <DCard>
                <TerminationList
                    v-if="tab === 'open'"
                    :terminations="terminations"
                    :page="page"
                    :pages="pages"
                    :order-values="orderValues"
                    @pageChanged="changePage"
                    @cancelTermination="cancelTermination"
                    @markAsCompleted="markTerminationAsCompleted"
                    @addBrandToFilter="addBrandToFilter"
                    @removeCheck="removeCheck"
                    @updateOrderValues="updateOrderValue"
                />
                <TerminationList
                    v-if="tab === 'due'"
                    :terminations="terminations"
                    :page="page"
                    :pages="pages"
                    :order-values="orderValues"
                    @pageChanged="changePage"
                    @cancelTermination="cancelTermination"
                    @markAsCompleted="markTerminationAsCompleted"
                    @addBrandToFilter="addBrandToFilter"
                    @removeCheck="removeCheck"
                    @updateOrderValues="updateOrderValue"
                />
                <TerminationList
                    v-if="tab === 'suspended'"
                    :terminations="terminations"
                    :page="page"
                    :pages="pages"
                    :order-values="orderValues"
                    with-suspended-at
                    @pageChanged="changePage"
                    @cancelTermination="cancelTermination"
                    @markAsCompleted="markTerminationAsCompleted"
                    @addBrandToFilter="addBrandToFilter"
                    @removeCheck="removeCheck"
                    @updateOrderValues="updateOrderValue"
                />
                <TerminationList
                    v-if="tab === 'completed'"
                    :terminations="terminations"
                    :page="page"
                    :pages="pages"
                    :order-values="orderValues"
                    with-suspended-at
                    with-completed-at
                    without-action-column
                    @pageChanged="changePage"
                    @cancelTermination="cancelTermination"
                    @addBrandToFilter="addBrandToFilter"
                    @removeCheck="removeCheck"
                    @updateOrderValues="updateOrderValue"
                />
            </DCard>
        </DCard>
    </div>
</template>

<script>
import TerminationList from '~@/Components/Contracts/Terminations/TerminationList'

export default {
    name: 'TerminationsIndex',
    components: { TerminationList },
    props: {
        selectedSegments: {
            type: Array,
            default: () => []
        },
        selectedBrands: {
            type: Array,
            default: () => []
        },
        segments: {
            type: Array,
            default: () => []
        },
        brands: {
            type: Array,
            default: () => []
        },
        terminations: {
            type: Array,
            default: () => []
        },
        page: {
            type: Number,
            default: 1
        },
        pages: {
            type: Number,
            default: 0
        },
        tab: {
            type: String,
            default: 'open'
        },
        counters: {
            type: Object,
            default: () => {}
        },
        selectedTypes: {
            type: Array,
            default: () => []
        },
        selectedReasons: {
            type: Array,
            default: () => []
        },
        types: {
            type: Array,
            default: () => []
        },
        reasons: {
            type: Array,
            default: () => []
        },
        orderValues: {
            type: Object,
            default: () => {}
        },
        modules: {
            type: Array,
            default () {
                return []
            }
        },
        contractStatuses: {
            type: Array,
            default () {
                return []
            }
        },
        selectedContractStatus: {
            type: String,
            default () {
                return null
            }
        },
        selectedModules: {
            type: Array,
            default () {
                return []
            }
        }
    },
    data () {
        return {
            tabIndex: {
                open: 0,
                due: 1,
                suspended: 2,
                completed: 3
            },
            queryString: this.$route().params.search,
            filterSegmentsOptions: this.getSegmentOptions(),
            filterBrandOptions: this.getBrandOptions(),
            selectedBrandList: this.selectedBrands,
            selectedSegmentsList: this.selectedSegments,
            filterTypesOptions: this.getTypesOptions(),
            filterReasonOptions: this.getReasonOptions(),
            selectedTypeList: this.selectedTypes,
            selectedReasonList: this.selectedReasons,
            selectedModulesList: this.selectedModules,
            selectedContractStatusList: this.selectedContractStatus,
            orderBy: this.orderValues.orderBy ?? '',
            orderByAsc: this.orderValues.orderByAsc ?? 0
        }
    },
    computed: {
        moduleOptions () {
            return this.modules.map((value) => {
                return { value, text: value, selected: this.selectedModules?.includes(String(value)) }
            })
        }
    },
    methods: {
        changePage (page) {
            this.$inertia.get(this.$route('contracts.terminations'), {
                page,
                tab: this.tab,
                brands: this.selectedBrandList,
                orderBy: this.orderBy,
                orderByAsc: +this.orderByAsc,
                segments: this.selectedSegmentsList,
                types: this.selectedTypeList,
                reasons: this.selectedReasonList,
                modules: this.selectedModulesList,
                contractStatus: this.selectedContractStatusList
            })
        },
        cancelTermination (terminationId) {
            this.$inertia.delete(this.$route('contracts.terminations.destroy', {
                termination: terminationId,
                _query: {
                    page: this.page,
                    tab: this.tab,
                    search: this.queryString,
                    brands: this.selectedBrandList
                }
            }))
        },
        markTerminationAsCompleted (terminationId) {
            this.$inertia.patch(this.$route('contracts.terminations.mark_completed', {
                termination: terminationId,
                _query: {
                    page: this.page,
                    tab: this.tab,
                    search: this.queryString,
                    brands: this.selectedBrandList
                }
            }))
        },
        removeCheck (terminationId) {
            this.$inertia.patch(this.$route('contracts.terminations.remove_check', {
                termination: terminationId,
                _query: {
                    page: this.page,
                    tab: this.tab,
                    search: this.queryString,
                    brands: this.selectedBrandList
                }
            }))
        },
        search () {
            return this.applyFilters()
        },
        onSegmentsFilterChange (selectedSegments) {
            this.selectedSegmentsList = selectedSegments
        },
        getSegmentOptions () {
            const filterSegmentOptions = []
            for (const [key] of Object.entries(this.segments)) {
                filterSegmentOptions.push({
                    value: this.segments[key].id,
                    text: this.segments[key].name,
                    selected: this.selectedSegments.includes(this.segments[key].id)
                })
            }
            return filterSegmentOptions
        },
        getTypesOptions () {
            const types = []
            for (const [value] of Object.entries(this.types)) {
                types.push({
                    value: this.types[value],
                    text: this.types[value],
                    selected: this.selectedTypes.includes(this.types[value])
                })
            }
            return types
        },
        getReasonOptions () {
            const reasons = []
            for (const [, value] of Object.entries(this.reasons)) {
                reasons.push({
                    value: value.id,
                    text: value.short_name,
                    selected: this.selectedReasons.includes(value.id)
                })
            }
            return reasons
        },
        onTypesFilterChange (selectedTypes) {
            this.selectedTypeList = selectedTypes
        },
        onReasonsFilterChange (selectedReasons) {
            this.selectedReasonList = selectedReasons
        },
        onContractStatusChange (contractStatus) {
            this.selectedContractStatusList = contractStatus
        },
        addSegmentToFilter (segmentId) {
            if (segmentId === null) {
                return
            }
            if (Object.values(this.selectedSegmentsList).indexOf(String(segmentId)) > -1) {
                return
            }

            this.selectedSegmentsList.push(String(segmentId))
        },
        onBrandsFilterChange (selectedBrands) {
            this.selectedBrandList = selectedBrands
        },
        onSelectedModulesChange (values) {
            this.selectedModulesList = values
        },
        getBrandOptions () {
            const filterBrandOptions = []
            for (const [key] of Object.entries(this.brands)) {
                filterBrandOptions.push({
                    value: this.brands[key].id,
                    text: this.brands[key].name,
                    selected: this.selectedBrands.includes(this.brands[key].id)
                })
            }
            return filterBrandOptions
        },
        addBrandToFilter (brandId) {
            if (brandId === null) {
                return
            }
            if (Object.values(this.selectedBrandList).indexOf(String(brandId)) > -1) {
                return
            }

            this.selectedBrandList.push(String(brandId))
        },
        resetFilters () {
            return this.$inertia.visit(this.$route('contracts.terminations', {
                tab: this.tab,
                save_filters: true,
                default_filters: true
            }))
        },
        applyFilters () {
            return this.$inertia.visit(this.$route('contracts.terminations',
                {
                    search: this.queryString,
                    tab: this.tab,
                    brands: this.selectedBrandList,
                    segments: this.selectedSegmentsList,
                    types: this.selectedTypeList,
                    reasons: this.selectedReasonList,
                    modules: this.selectedModulesList,
                    contractStatus: this.selectedContractStatusList,
                    orderBy: this.orderBy,
                    orderByAsc: this.orderByAsc,
                    save_filters: true
                }))
        },
        updateOrderValue (column, orderByAsc) {
            this.orderBy = column
            this.orderByAsc = orderByAsc
            this.applyFilters()
        },
        createRouteForTerminationTab (tab) {
            return this.$route('contracts.terminations', {
                page: 1,
                tab: tab,
                search: this.queryString,
                modules: this.selectedModules,
                segments: this.selectedSegments,
                types: this.selectedTypes,
                reasons: this.selectedReasons,
                brands: this.selectedBrandList,
                contractStatus: this.selectedContractStatus
            })
        }
    }
}
</script>
